import React, { useState, useEffect, useCallback } from 'react';
import './Dashboard.css'; 
import { api, ApiError } from './api';
import { EstadoError } from './EstadoError';

export default function Dashboard({ usuario, setVista, setFiltroInventario, branding, nombreSucursal, nombreEmpresa }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const tituloSucursal = nombreSucursal || usuario?.sucursal_nombre || branding?.nombre_negocio || 'Sucursal';
  const subtituloSucursal = nombreEmpresa && nombreEmpresa !== tituloSucursal
    ? `Sucursal de ${nombreEmpresa}`
    : 'Panel de control del comercio';

  const cargarDatos = useCallback(async () => {
    setError('');
    try {
      // ANTES: fetch(...).then(res => res.json()).then(setData) — sin
      // chequear nunca si la respuesta era un error. Si el backend devolvía
      // { error: "..." } con status 500, ese objeto se guardaba igual en
      // setData(), y dos líneas más abajo "data.metodos_pago" no existía:
      // React explotaba al desestructurar y, sin ErrorBoundary, tiraba
      // abajo TODA la app (no solo el Dashboard) → pantalla en blanco.
      const resData = await api.get('/api/dashboard');
      setData(resData);
    } catch (err) {
      setError(err instanceof ApiError ? err.mensaje : 'No se pudo cargar el panel de control.');
    }
  }, []);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const formatearDinero = (monto) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(monto || 0);
  const formatearHora = (fecha) => new Date(fecha).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });

  if (error) return <EstadoError mensaje={error} onReintentar={cargarDatos} />;
  if (!data) return <div style={{ padding: '40px', color: '#64748b', textAlign: 'center' }}>Cargando panel de control en tiempo real...</div>;

  // Defaults defensivos: aunque el backend ya garantiza esta forma
  // (ver dashboard.controller.js), nunca está de más no asumir ciegamente
  // la forma exacta de una respuesta de red.
  const metodosPago = data.metodos_pago || {};
  const { efectivo = 0, tarjeta = 0, mercadoPago = 0, cuentaCorriente = 0 } = metodosPago;
  const kpis = data.kpis || {};
  const alertas = data.alertas || {};
  const ultimasVentas = Array.isArray(data.ultimas_ventas) ? data.ultimas_ventas : [];
  const ultimosMovimientos = Array.isArray(data.ultimos_movimientos) ? data.ultimos_movimientos : [];
  const promosActivas = Array.isArray(data.promos_activas) ? data.promos_activas : [];

  const totalMetodos = efectivo + tarjeta + mercadoPago + cuentaCorriente;
  
  const pEfe = totalMetodos > 0 ? (efectivo / totalMetodos) * 100 : 0;
  const pTar = totalMetodos > 0 ? (tarjeta / totalMetodos) * 100 : 0;
  const pMp = totalMetodos > 0 ? (mercadoPago / totalMetodos) * 100 : 0;
  const pCc = totalMetodos > 0 ? (cuentaCorriente / totalMetodos) * 100 : 0;

  const donutStyle = totalMetodos > 0 
    ? { background: `conic-gradient(var(--c-efectivo) 0% ${pEfe}%, var(--c-tarjeta) ${pEfe}% ${pEfe+pTar}%, var(--c-mp) ${pEfe+pTar}% ${pEfe+pTar+pMp}%, var(--c-cc) ${pEfe+pTar+pMp}% 100%)` }
    : { background: '#e2e8f0' };

  return (
    <div className="dash-container">
      <div style={{ display: 'flex', alignItems: 'center', gap: '18px', padding: branding?.personalizado ? '16px 20px' : '0', marginBottom: branding?.personalizado ? '18px' : '0', background: branding?.personalizado ? 'white' : 'transparent', borderRadius: '14px', border: branding?.personalizado ? '1px solid #e2e8f0' : 'none' }}>
        {branding?.personalizado && branding?.logo_url && <img src={branding.logo_url} alt={tituloSucursal} style={{ maxWidth: '210px', maxHeight: '76px', objectFit: 'contain' }} />}
        {branding?.personalizado && <div><div style={{ fontSize: '22px', fontWeight: 800, color: '#0f172a' }}>{tituloSucursal}</div><div style={{ color: '#64748b', fontSize: '13px' }}>{subtituloSucursal}</div></div>}
      </div>
      <div className="kpi-grid">
        <KpiCard title="Ventas de hoy" value={formatearDinero(kpis.ventasHoy)} subtext={`${kpis.vCrecimiento > 0 ? '↑' : '↓'} ${kpis.vCrecimiento || 0}% vs ayer`} icon="💰" color="#3b82f6" />
        <KpiCard title="Tickets del día" value={kpis.ticketsHoy || 0} subtext={`${kpis.tCrecimiento > 0 ? '↑' : '↓'} ${kpis.tCrecimiento || 0}% vs ayer`} icon="🧾" color="#8b5cf6" />
        <KpiCard title="Ganancia estimada" value={formatearDinero(kpis.gananciaHoy)} subtext={`${kpis.gCrecimiento > 0 ? '↑' : '↓'} ${kpis.gCrecimiento || 0}% vs ayer`} icon="📈" color="#10b981" />
        <KpiCard title="Caja actual" value={formatearDinero(kpis.cajaActual)} subtext={data.estado_caja === 'Abierta' ? `Efectivo en caja` : 'Caja Cerrada'} icon="💵" color="#0ea5e9" />
        <KpiCard title="Clientes con deuda" value={kpis.clientesDeuda || 0} subtext={`${formatearDinero(kpis.montoDeuda)} pendientes`} icon="👥" color="#f59e0b" />
        <KpiCard title="Stock bajo" value={kpis.stockBajo || 0} subtext="Productos críticos" icon="⚠️" color="#ef4444" />
      </div>

      <div className="split-grid">
        <div className="card-panel">
          <h3 className="card-title">Ventas por método de pago <span style={{fontSize:'12px', color:'#64748b', backgroundColor:'#f1f5f9', padding:'4px 8px', borderRadius:'10px'}}>Hoy</span></h3>
          
          <div className="donut-container">
            <div className="donut-chart" style={donutStyle}>
              <div className="donut-inner">
                <span style={{color:'#64748b', fontSize:'12px'}}>Total</span>
                <strong style={{fontSize:'20px', color:'#0f172a'}}>{formatearDinero(kpis.ventasHoy)}</strong>
              </div>
            </div>
          </div>

          <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
            <LegendItem label="Efectivo" amount={formatearDinero(efectivo)} percent={`${pEfe.toFixed(1)}%`} color="#10b981" />
            <LegendItem label="Tarjeta" amount={formatearDinero(tarjeta)} percent={`${pTar.toFixed(1)}%`} color="#3b82f6" />
            <LegendItem label="Mercado Pago" amount={formatearDinero(mercadoPago)} percent={`${pMp.toFixed(1)}%`} color="#8b5cf6" />
            <LegendItem label="Cuenta Corriente" amount={formatearDinero(cuentaCorriente)} percent={`${pCc.toFixed(1)}%`} color="#f59e0b" />
          </div>
        </div>

        <div className="card-panel">
          <h3 className="card-title">Accesos rápidos</h3>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'15px'}}>
            <QuickBtn label="Nueva venta" icon="🛒" bg="#eff6ff" txt="#1d4ed8" action={() => setVista('ventas')} />
            <QuickBtn label="Importar precios" icon="📦" bg="#f0fdf4" txt="#15803d" action={() => setVista('inventario')} />
            <QuickBtn label="Nuevo cliente" icon="👥" bg="#faf5ff" txt="#7e22ce" action={() => setVista('clientes')} />
            <QuickBtn label="Registrar compra" icon="🚚" bg="#fefce8" txt="#a16207" action={() => setVista('proveedores')} />
            <QuickBtn label={data.estado_caja === 'Abierta' ? 'Cerrar caja' : 'Abrir Caja'} icon="🔒" bg="#fef2f2" txt="#b91c1c" action={() => setVista('caja')} />
            <QuickBtn label="Ver faltantes" icon="⚠️" bg="#fff7ed" txt="#c2410c" action={() => setVista('reportes')} />
          </div>
        </div>
      </div>

      <div className="split-grid">
        <div className="card-panel">
          <h3 className="card-title">Últimas ventas <button style={btnLink} onClick={()=>setVista('historial')}>Ver todas</button></h3>
          <div style={{overflowX:'auto'}}>
            <table className="table-modern">
              <thead><tr><th>Ticket</th><th>Hora</th><th>Cliente</th><th>Total</th><th>Medio</th></tr></thead>
              <tbody>
                {ultimasVentas.map(v => (
                  <tr key={v.id}>
                    <td><strong>{`${v.ticket_serie || '0001'}-${String(v.ticket_numero || v.id).padStart(8, '0')}`}</strong></td>
                    <td>{formatearHora(v.fecha)}</td>
                    <td>{v.cliente || 'Consumidor Final'}</td>
                    <td style={{fontWeight:'bold'}}>{formatearDinero(v.total)}</td>
                    <td><Badge bg={v.metodo_pago === 'Efectivo' ? '#d1fae5' : '#f1f5f9'} color={v.metodo_pago === 'Efectivo' ? '#065f46' : '#475569'}>{v.metodo_pago}</Badge></td>
                  </tr>
                ))}
                {ultimasVentas.length === 0 && <tr><td colSpan="5" style={{textAlign:'center', color:'#94a3b8'}}>No hay ventas recientes</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card-panel">
          <h3 className="card-title">Movimientos de caja (Turno) <button style={btnLink} onClick={()=>setVista('caja')}>Ver todos</button></h3>
          <div style={{overflowX:'auto'}}>
            <table className="table-modern">
              <thead><tr><th>Hora</th><th>Tipo</th><th>Descripción</th><th style={{textAlign:'right'}}>Monto</th></tr></thead>
              <tbody>
                {ultimosMovimientos.map((m, i) => (
                  <tr key={i}>
                    <td>{formatearHora(m.fecha)}</td>
                    <td>
                      {m.tipo === 'Ingreso' 
                        ? <span style={{color:'#10b981', fontWeight:'bold'}}>↓ Ingreso</span> 
                        : <span style={{color:'#ef4444', fontWeight:'bold'}}>↑ Egreso</span>}
                    </td>
                    <td>{m.descripcion}</td>
                    <td style={{textAlign:'right', color: m.tipo === 'Ingreso' ? '#10b981' : '#ef4444', fontWeight:'bold'}}>
                      {m.tipo === 'Ingreso' ? '+' : '-'}{formatearDinero(m.monto)}
                    </td>
                  </tr>
                ))}
                {ultimosMovimientos.length === 0 && <tr><td colSpan="4" style={{textAlign:'center', color:'#94a3b8'}}>Sin movimientos en el turno activo</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="split-grid">
        <div className="card-panel">
          <h3 className="card-title">Alertas del sistema</h3>
          <div className="alerts-grid">
            <AlertCard icon="📦" text="Stock bajo" number={`${alertas.stockBajo || 0} prod`} bg="#fefce8" color="#a16207" action={() => { setFiltroInventario('bajo'); setVista('inventario'); }} />
            <AlertCard icon="📉" text="Stock negativo" number={`${alertas.stockNegativo || 0} prod`} bg="#fef2f2" color="#b91c1c" action={() => { setFiltroInventario('negativo'); setVista('inventario'); }} />
            <AlertCard icon="👥" text="Clientes deuda" number={`${alertas.clientesDeuda || 0} cli`} bg="#fff7ed" color="#c2410c" action={() => setVista('clientes')} />
            <AlertCard icon="🏷️" text="Sin precio act." number={`${alertas.sinPrecio || 0} prod`} bg="#f0f9ff" color="#0369a1" action={() => { setFiltroInventario('sin_precio'); setVista('inventario'); }} />
            <AlertCard icon="⏳" text="Próximos a vencer" number={`${alertas.lotesProximos || 0} lotes`} bg="#fffbeb" color="#b45309" action={() => setVista('lotes')} />
            <AlertCard icon="🚫" text="Lotes vencidos" number={`${alertas.lotesVencidos || 0} lotes`} bg="#fef2f2" color="#b91c1c" action={() => setVista('lotes')} />
          </div>
        </div>

        {/* 🚀 PANEL DE PROMOCIONES VIGENTES */}
        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '10px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
          <h3 style={{ margin: '0 0 15px 0', borderBottom: '2px solid #e2e8f0', paddingBottom: '10px', color: '#0f172a' }}>
            🎁 Promociones Vigentes en Caja
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', overflowY: 'auto', maxHeight: '200px', paddingRight: '5px' }}>
            {promosActivas.length > 0 ? (
              promosActivas.map((promo, idx) => (
                <div key={idx} style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', padding: '12px', borderRadius: '8px' }}>
                  <strong style={{ display: 'block', color: '#166534', fontSize: '15px' }}>{promo.nombre}</strong>
                  <span style={{ fontSize: '13px', color: '#15803d', display: 'block', marginTop: '2px' }}>
                    {promo.tipo === '2x1' ? 'Mecánica: 2x1' : promo.tipo === 'combo_fijo' ? 'Combo Especial' : 'Descuento Directo'}
                  </span>
                  
                  <div style={{ marginTop: '6px', padding: '6px 8px', backgroundColor: '#dcfce3', borderRadius: '4px', fontSize: '12px', color: '#166534' }}>
                    <strong>🛒 Aplica a: </strong> {promo.productos_incluidos || 'Todos los productos'}
                  </div>
                  
                  <small style={{ display: 'block', marginTop: '6px', color: '#15803d', fontWeight: 'bold' }}>
                    Vence: {promo.fecha_fin ? new Date(promo.fecha_fin).toLocaleDateString('es-AR') : 'Sin vencimiento'}
                  </small>
                </div>
              ))
            ) : (
              <p style={{ color: '#94a3b8', fontStyle: 'italic', textAlign: 'center', marginTop: '30px' }}>No hay promociones activas.</p>
            )}
          </div>
        </div>
      </div>

    </div>
  );
}

const KpiCard = ({ title, value, subtext, icon, color }) => (
  <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '16px', boxShadow: 'var(--shadow-md)', display: 'flex', alignItems: 'center', gap: '15px' }}>
    <div style={{ backgroundColor: `${color}15`, minWidth: '48px', height: '48px', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '24px' }}>{icon}</div>
    <div style={{ overflow: 'hidden' }}>
      <p style={{ margin: '0 0 5px 0', color: '#64748b', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap' }}>{title}</p>
      <h3 style={{ margin: 0, color: '#0f172a', fontSize: '22px', fontWeight: 'bold' }}>{value}</h3>
      <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: subtext.includes('↑') ? '#10b981' : (subtext.includes('↓') ? '#ef4444' : '#64748b'), fontWeight:'500' }}>{subtext}</p>
    </div>
  </div>
);

const LegendItem = ({ label, amount, percent, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '14px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: color }}></div>
      <span style={{ color: '#475569' }}>{label}</span>
    </div>
    <div style={{ display: 'flex', gap: '15px' }}>
      <span style={{ fontWeight: '600', color: '#0f172a', width:'80px', textAlign:'right' }}>{amount}</span>
      <span style={{ color: '#64748b', width:'45px', textAlign:'right' }}>{percent}</span>
    </div>
  </div>
);

const QuickBtn = ({ label, icon, bg, txt, action }) => (
  <button onClick={action} style={{ backgroundColor: bg, color: txt, border: 'none', padding: '16px', borderRadius: '12px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', transition: 'transform 0.1s' }} onMouseOver={e => e.currentTarget.style.transform = 'scale(1.02)'} onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}>
    <span style={{ fontSize: '24px' }}>{icon}</span>
    <span style={{ fontSize: '13px', fontWeight: '600', textAlign: 'center' }}>{label}</span>
  </button>
);

const AlertCard = ({ icon, text, number, bg, color, action }) => (
  <div onClick={action} style={{ backgroundColor: bg, padding: '15px', borderRadius: '12px', borderLeft: `4px solid ${color}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <span style={{ fontSize: '20px' }}>{icon}</span>
      <div>
        <strong style={{ display: 'block', color: color, fontSize: '14px' }}>{text}</strong>
        <span style={{ fontSize: '13px', color: '#64748b' }}>{number}</span>
      </div>
    </div>
    <span style={{ color: color, fontSize:'16px' }}>➔</span>
  </div>
);

const Badge = ({ children, bg, color }) => (
  <span style={{ backgroundColor: bg, color: color, padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}>{children}</span>
);

const btnLink = { background:'none', border:'none', color:'#3b82f6', fontWeight:'600', cursor:'pointer', fontSize:'14px' };